import { compareText } from '../model/compareText'
import { shortInstitutionName } from '../model/institutionName'
import {
  type AccountAsOf,
  type RollupBadge,
  rollupBadge,
} from '../model/portfolioOverview'
import type { MonthKey } from '../model/types'

// Past this many accounts the table groups them by institution.
export const GROUP_PAST = 8
// Past this many accounts the table gets a search box and groups start folded.
export const SEARCH_PAST = 25
export const PAGE_SIZE = 25

export type AccountSortKey =
  | 'name'
  | 'statements'
  | 'value'
  | 'change'
  | 'netMoney'

export interface AccountSort {
  key: AccountSortKey
  direction: 'asc' | 'desc'
}

export const DEFAULT_SORT: AccountSort = { key: 'value', direction: 'desc' }

export interface InstitutionGroup {
  institution: string
  accounts: AccountAsOf[]
  endedCount: number
  statementCount: number
  firstStatementMonth: MonthKey | null
  lastStatementMonth: MonthKey | null
  // Null when any open account in the group has no value this month.
  value: number | null
  change: number | null
  netMoney: number
  badge: RollupBadge
}

export type TableLine =
  | { kind: 'group'; group: InstitutionGroup; isOpen: boolean }
  | { kind: 'account'; account: AccountAsOf; inGroup: boolean }

interface SortableFigures {
  name: string
  statementCount: number
  value: number | null
  change: number | null
  netMoney: number
}

const figuresOfAccount = (account: AccountAsOf): SortableFigures => ({
  name: account.name,
  statementCount: account.statementCount,
  value: account.value,
  change: account.change?.amount ?? null,
  netMoney: account.money.netMoney,
})

const figuresOfGroup = (group: InstitutionGroup): SortableFigures => ({
  name: group.institution,
  statementCount: group.statementCount,
  value: group.value,
  change: group.change,
  netMoney: group.netMoney,
})

const SORT_FIELD: Record<AccountSortKey, keyof SortableFigures> = {
  name: 'name',
  statements: 'statementCount',
  value: 'value',
  change: 'change',
  netMoney: 'netMoney',
}

// Empty figures always sort last, whichever way the column runs.
const compareFigures = (
  first: SortableFigures,
  second: SortableFigures,
  sort: AccountSort,
) => {
  const field = SORT_FIELD[sort.key]
  const firstValue = first[field]
  const secondValue = second[field]
  if (firstValue == null || secondValue == null) {
    return (
      Number(firstValue == null) - Number(secondValue == null) ||
      compareText(first.name, second.name)
    )
  }
  const sign = sort.direction === 'asc' ? 1 : -1
  const order =
    typeof firstValue === 'string' && typeof secondValue === 'string'
      ? compareText(firstValue.toLowerCase(), secondValue.toLowerCase())
      : Number(firstValue) - Number(secondValue)
  return sign * order || compareText(first.name, second.name)
}

const sortAccounts = (accounts: AccountAsOf[], sort: AccountSort) =>
  [...accounts].sort((first, second) =>
    compareFigures(figuresOfAccount(first), figuresOfAccount(second), sort),
  )

export const matchesSearch = (account: AccountAsOf, search: string) => {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [account.name, account.institution, account.accountType, account.last4]
    .join(' ')
    .toLowerCase()
    .includes(needle)
}

const sumCents = (amounts: number[]) =>
  amounts.reduce((sum, amount) => sum + Math.round(amount * 100), 0) / 100

// Any account with no value that month, a month before its first statement included, leaves the
// group with no total.
const groupValue = (accounts: AccountAsOf[]) =>
  accounts.some((account) => account.value == null)
    ? null
    : sumCents(accounts.map((account) => account.value ?? 0))

const groupChange = (accounts: AccountAsOf[]) =>
  accounts.length === 0 || accounts.some((account) => account.change == null)
    ? null
    : sumCents(accounts.map((account) => account.change?.amount ?? 0))

const monthBound = (months: (MonthKey | null)[], pick: 'first' | 'last') => {
  const known = months
    .filter((month): month is MonthKey => month != null)
    .sort()
  return (pick === 'first' ? known[0] : known[known.length - 1]) ?? null
}

const buildGroup = (
  institution: string,
  accounts: AccountAsOf[],
): InstitutionGroup => {
  return {
    institution,
    accounts,
    endedCount: accounts.filter((account) => account.status === 'ended').length,
    statementCount: accounts.reduce(
      (sum, account) => sum + account.statementCount,
      0,
    ),
    firstStatementMonth: monthBound(
      accounts.map((account) => account.firstStatementMonth),
      'first',
    ),
    lastStatementMonth: monthBound(
      accounts.map((account) => account.lastStatementMonth),
      'last',
    ),
    value: groupValue(accounts),
    change: groupChange(accounts),
    netMoney: sumCents(accounts.map((account) => account.money.netMoney)),
    badge: rollupBadge(accounts),
  }
}

export const groupByInstitution = (
  accounts: AccountAsOf[],
): InstitutionGroup[] => {
  const byInstitution = new Map<string, AccountAsOf[]>()
  for (const account of accounts) {
    const institution = shortInstitutionName(account.institution)
    byInstitution.set(institution, [
      ...(byInstitution.get(institution) ?? []),
      account,
    ])
  }
  return Array.from(byInstitution, ([institution, members]) =>
    buildGroup(institution, members),
  )
}

const sortedGroups = (accounts: AccountAsOf[], sort: AccountSort) =>
  groupByInstitution(accounts).sort((first, second) =>
    compareFigures(figuresOfGroup(first), figuresOfGroup(second), sort),
  )

// The order the Accounts table first lists the accounts in. Holdings, awards and year to date follow it.
export const accountsInTableOrder = (
  accounts: AccountAsOf[],
  sort: AccountSort = DEFAULT_SORT,
): AccountAsOf[] =>
  accounts.length > GROUP_PAST
    ? sortedGroups(accounts, sort).flatMap((group) =>
        sortAccounts(group.accounts, sort),
      )
    : sortAccounts(accounts, sort)

export interface TableLinesInput {
  accounts: AccountAsOf[]
  sort: AccountSort
  search: string
  grouped: boolean
  // Groups the reader opened or folded, against the default.
  toggledGroups: Set<string>
  selectedAccountId: string | null
}

const groupIsOpen = (
  group: InstitutionGroup,
  input: TableLinesInput,
  foldedByDefault: boolean,
) => {
  if (input.search.trim()) return true
  const holdsSelection = group.accounts.some(
    (account) => account.accountId === input.selectedAccountId,
  )
  const toggled = input.toggledGroups.has(group.institution)
  return holdsSelection || (foldedByDefault ? toggled : !toggled)
}

// The rows the table shows, in order: group rows with their open accounts, or a flat list.
export const tableLines = (input: TableLinesInput): TableLine[] => {
  const found = input.accounts.filter((account) =>
    matchesSearch(account, input.search),
  )
  if (!input.grouped) {
    return sortAccounts(found, input.sort).map((account) => ({
      kind: 'account',
      account,
      inGroup: false,
    }))
  }
  const foldedByDefault = input.accounts.length > SEARCH_PAST
  return sortedGroups(found, input.sort).flatMap((group): TableLine[] => {
    const isOpen = groupIsOpen(group, input, foldedByDefault)
    const children: TableLine[] = isOpen
      ? sortAccounts(group.accounts, input.sort).map((account) => ({
          kind: 'account',
          account,
          inGroup: true,
        }))
      : []
    return [{ kind: 'group', group, isOpen }, ...children]
  })
}

export interface LinePage {
  lines: TableLine[]
  pageIndex: number
  pageCount: number
  firstLine: number
  lastLine: number
  total: number
}

export const pageOfLines = (
  lines: TableLine[],
  pageIndex: number,
): LinePage => {
  const pageCount = Math.max(1, Math.ceil(lines.length / PAGE_SIZE))
  const safeIndex = Math.min(Math.max(pageIndex, 0), pageCount - 1)
  const start = safeIndex * PAGE_SIZE
  const pageLines = lines.slice(start, start + PAGE_SIZE)
  return {
    lines: pageLines,
    pageIndex: safeIndex,
    pageCount,
    firstLine: lines.length === 0 ? 0 : start + 1,
    lastLine: start + pageLines.length,
    total: lines.length,
  }
}
